/**
 * @fileoverview Shared Note schema used by both frontend and backend to ensure
 * validation rules stay perfectly synchronized.
 */

import { z } from 'zod';
import { archivableAccountTrackingFields, archivedAtSchema, commonValidators, createCrudSchemas, createEntityIdSchema, createOptionalDatetimeSchema, createdAtSchema, deletedAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { ID_PATTERNS, PriorityEnum } from './types.js';

/** Maximum length for note content (characters) */
export const NOTE_CONTENT_MAX_LENGTH = 100_000;

/** Valid Fibonacci story point values for agile estimation */
export const FIBONACCI_STORY_POINTS = [1, 2, 3, 5, 8, 13, 21] as const;

/**
 * Note validation schema - aligned with database note table.
 * Uses VARCHAR(5) nanoid5 format for id.
 */
export const NoteSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.NOTE, 'ID must be in nanoid5 format'),
  title: commonValidators.requiredString('Title', 255),
  content: z.string().max(NOTE_CONTENT_MAX_LENGTH).optional().nullable(),
  version_number: z.number().int().positive().default(1),
  priority: PriorityEnum.default('Medium'),
  cognitive_load: z.number().int().min(0).max(5).default(3),
  status_id: commonValidators.foreignKey('Status ID'),
  person_id: commonValidators.foreignKey('Person ID'),
  kind_id: commonValidators.foreignKey('Kind ID'),
  project_id: commonValidators.foreignKey('Project ID'),
  resource_id: commonValidators.foreignKey('Resource ID'),
  area_id: commonValidators.foreignKey('Area ID'),
  category_id: commonValidators.foreignKey('Category ID'),
  subcategory_id: commonValidators.foreignKey('Subcategory ID'),
  organisation_id: commonValidators.foreignKey('Organisation ID'),
  parent_note_id: commonValidators.noteForeignKey('Parent Note ID'),
  estimated_minutes: z.number().int().positive().optional().nullable(),
  story_points: z.number().int().refine(
    (val) => val === null || (FIBONACCI_STORY_POINTS as readonly number[]).includes(val),
    { message: `Story points must be one of: ${FIBONACCI_STORY_POINTS.join(', ')} (Fibonacci sequence)` }
  ).optional().nullable(),
  progress: z.number().int().min(0).max(100).default(0),
  metadata: commonValidators.jsonField('Metadata'),
  search_vector: z.string().optional().nullable(),
  embedding: z.string().optional().nullable(),
  summary: z.string().max(120).optional().nullable(),
  quality_score: z.number().int().min(0).max(100).optional().nullable(),
  is_pinned: commonValidators.booleanWithDefault(false),
  is_bookmarked: commonValidators.booleanWithDefault(false),
  is_flagged: commonValidators.booleanWithDefault(false),
  is_milestone: commonValidators.booleanWithDefault(false),
  start_date: commonValidators.dateString(),
  due_date: commonValidators.dateString(),
  completed_at: createOptionalDatetimeSchema('Completed at'),
  ...archivableAccountTrackingFields,
  archived_at: archivedAtSchema,
  deleted_at: deletedAtSchema,
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

const { CreateSchema, UpdateSchema } = createCrudSchemas(NoteSchema);

export const NoteCreateSchema = CreateSchema;
export const NoteUpdateSchema = UpdateSchema;

export type Note = z.infer<typeof NoteSchema>;
export type NoteCreate = z.infer<typeof NoteCreateSchema>;
export type NoteUpdate = z.infer<typeof NoteUpdateSchema>;

export const NOTE_FIELDS = getSchemaFields(NoteSchema);
